import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import Select from "react-select";

const getStatusConfig = (result) => {
  if (!result?.success) {
    return {
      title: "Upload failed",
      alertClass: "alert alert-danger",
      iconClass: "ri-close-circle-line text-danger",
    };
  }

  return {
    title: "Update completed",
    alertClass: "alert alert-success",
    iconClass: "ri-checkbox-circle-line text-success",
  };
};

const StockCheckUploadModal = ({
  show,
  onCloseClick,
  onSubmit,
  onSuccess,
  suppliers,
  initialSupplier,
}) => {
  const [selectedSupplier, setSelectedSupplier] = useState(initialSupplier || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!show) {
      setSelectedFile(null);
      setResult(null);
      setUploading(false);
      setValidationError("");
      return;
    }

    setSelectedSupplier(initialSupplier || null);
    setSelectedFile(null);
    setResult(null);
    setUploading(false);
    setValidationError("");
  }, [show, initialSupplier]);

  const selectedFileName = useMemo(() => {
    return selectedFile ? selectedFile.name : "";
  }, [selectedFile]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const isExcelFile = file.name.toLowerCase().endsWith(".xlsx");

    if (!isExcelFile) {
      setSelectedFile(null);
      setValidationError("Please select a .xlsx file.");
      return;
    }

    setSelectedFile(file);
    setValidationError("");
  };

  const handleUpload = async () => {
    if (!selectedSupplier) {
      setValidationError("Please select a supplier.");
      return;
    }

    if (!selectedFile) {
      setValidationError("Please select an Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("sup_id", selectedSupplier.value);

    setUploading(true);
    setValidationError("");

    try {
      const response = await onSubmit(formData);

      setResult(
        response || {
          success: false,
          message: "The server returned an empty response.",
        }
      );

      if (response?.success) {
        onSuccess?.(response);
      }
    } catch (error) {
      setResult({
        success: false,
        message: error?.message || "Unexpected error while uploading the file.",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setSelectedFile(null);
    setValidationError("");
  };

  const statusConfig = getStatusConfig(result);
  const summary = result?.data || {};

  return (
    <Modal isOpen={show} toggle={onCloseClick} centered={true} size="lg">
      <ModalHeader toggle={onCloseClick}>
        {result ? "Stock Check Upload Results" : "Upload Stock Check File"}
      </ModalHeader>
      <ModalBody className="py-4 px-4">
        {!result ? (
          <div>
            <div className="mb-3">
              <label htmlFor="stockcheck-supplier" className="form-label">
                Supplier
              </label>
              <Select
                inputId="stockcheck-supplier"
                placeholder="Select supplier"
                options={suppliers}
                value={selectedSupplier}
                onChange={(option) => {
                  setSelectedSupplier(option);
                  setValidationError("");
                }}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="stockcheck-file" className="form-label">
                Excel file
              </label>
              <input
                id="stockcheck-file"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="form-control"
                onChange={handleFileChange}
              />
              <div className="form-text">
                Upload a .xlsx file with ASIN or UPC plus the stock check columns to update.
              </div>
              {selectedFileName ? (
                <div className="mt-2 small text-muted">Selected file: {selectedFileName}</div>
              ) : null}
            </div>

            {validationError ? <div className="alert alert-danger mb-0">{validationError}</div> : null}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button type="button" className="btn btn-light" onClick={onCloseClick} disabled={uploading}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Uploading
                  </>
                ) : (
                  <>
                    <i className="ri-upload-2-line me-1"></i>
                    Upload file
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className={statusConfig.alertClass} role="alert">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className={`${statusConfig.iconClass} fs-4`}></i>
                <h5 className="mb-0">{statusConfig.title}</h5>
              </div>
              <div>{result.message || "The upload has finished."}</div>
            </div>

            <div className="border rounded p-3 bg-light mb-3">
              <h6 className="mb-3">Summary</h6>
              <div className="row text-center">
                <div className="col-md-4 col-12 mb-3 mb-md-0">
                  <div className="text-success fs-3 fw-bold">{summary.updated || 0}</div>
                  <div className="text-muted small">Updated</div>
                </div>
                <div className="col-md-4 col-12 mb-3 mb-md-0">
                  <div className="text-warning fs-3 fw-bold">{summary.notFound || 0}</div>
                  <div className="text-muted small">Not found</div>
                </div>
                <div className="col-md-4 col-12">
                  <div className="text-danger fs-3 fw-bold">{summary.failed || 0}</div>
                  <div className="text-muted small">Failed</div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button type="button" className="btn btn-light" onClick={handleReset}>
                Upload another file
              </button>
              <button type="button" className="btn btn-primary" onClick={onCloseClick}>
                Close
              </button>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

StockCheckUploadModal.propTypes = {
  initialSupplier: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onCloseClick: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  show: PropTypes.bool.isRequired,
  suppliers: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    })
  ).isRequired,
};

StockCheckUploadModal.defaultProps = {
  initialSupplier: null,
  onSuccess: undefined,
};

export default StockCheckUploadModal;